import { useEffect, useMemo, useRef, useState } from 'react';
import { LuChevronDown } from 'react-icons/lu';
import type { Country } from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';

import SbrIcon from './SbrIcon';

type CountrySelectOption = {
  value?: Country;
  label: string;
  divider?: boolean;
};

type Props = {
  value?: Country;
  onChange: (value?: Country) => void;
  options: CountrySelectOption[];
  className?: string;
};

function parseCountryLabel(label: string): { name: string; dialCode: string } {
  const match = label.match(/^(.+?)\s\+(\d+)$/);
  if (match) {
    return { name: match[1].trim(), dialCode: `+${match[2]}` };
  }
  return { name: label.trim(), dialCode: '' };
}

function CountryFlag({ country, title }: { country: Country; title?: string }) {
  const Flag = flags[country];
  if (!Flag) return null;

  return (
    <span className="get-started-modal__country-flag" aria-hidden>
      <Flag title={title ?? country} />
    </span>
  );
}

export default function GetStartedCountrySelect({ value, onChange, options, className }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const countryOptions = useMemo(
    () => options.filter((option): option is CountrySelectOption & { value: Country } => Boolean(option.value)),
    [options],
  );

  const selectedCountry = value ?? countryOptions[0]?.value;

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={`get-started-modal__country-dropdown${open ? ' get-started-modal__country-dropdown--open' : ''}${className ? ` ${className}` : ''}`}
    >
      <button
        type="button"
        className="get-started-modal__country-toggle"
        aria-label="Select country code"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {selectedCountry && <CountryFlag country={selectedCountry} />}
        <SbrIcon icon={LuChevronDown} size={14} className="get-started-modal__country-chevron" />
      </button>

      {open && (
        <ul className="get-started-modal__country-menu" role="listbox" aria-label="Countries">
          {countryOptions.map((option) => {
            const { name, dialCode } = parseCountryLabel(option.label);
            const isActive = option.value === selectedCountry;

            return (
              <li key={option.value} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`get-started-modal__country-option${isActive ? ' get-started-modal__country-option--active' : ''}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <CountryFlag country={option.value} title={name} />
                  <span className="get-started-modal__country-option-name">{name}</span>
                  {dialCode && (
                    <span className="get-started-modal__country-option-code">{dialCode}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
